import React, { useEffect } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';

// Animation Variants
const heroVariants = {
  hidden: { 
    opacity: 0, 
    y: 100,
    scale: 0.9
  },
  visible: { 
    opacity: 1, 
    y: 0,
    scale: 1,
    transition: {
      duration: 1.2,
      ease: [0.25, 0.46, 0.45, 0.94],
      staggerChildren: 0.3
    }
  }
};

const titleVariants = {
  hidden: { 
    opacity: 0, 
    y: 50,
    scale: 0.95
  },
  visible: { 
    opacity: 1, 
    y: 0,
    scale: 1,
    transition: {
      duration: 0.8,
      ease: [0.25, 0.46, 0.45, 0.94]
    }
  }
};

const subtitleVariants = {
  hidden: { 
    opacity: 0, 
    y: 30
  },
  visible: { 
    opacity: 1, 
    y: 0,
    transition: {
      duration: 0.6,
      ease: [0.25, 0.46, 0.45, 0.94],
      delay: 0.2
    }
  }
};

const buttonVariants = {
  hidden: { 
    opacity: 0, 
    y: 20,
    scale: 0.9
  },
  visible: { 
    opacity: 1, 
    y: 0,
    scale: 1,
    transition: {
      duration: 0.5,
      ease: [0.25, 0.46, 0.45, 0.94],
      delay: 0.4
    }
  }
};

const HeroContainer = styled.section`
  position: relative;
  background-image: url('/images/Black logo name.jpg');
  background-size: cover;
  background-position: center;
  height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--highlight-color);
  text-align: center;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(rgba(0, 0, 0, 0.6), rgba(0, 0, 0, 0.8));
    z-index: 1;
  }

  > div {
    position: relative;
    z-index: 2;
    transition: opacity 0.3s ease;
    opacity: ${({ scrollOpacity }) => scrollOpacity};
  }
`;

const HeroTitle = styled.h1`
  font-family: 'Poppins', sans-serif;
  font-size: 5rem;
  font-weight: bold;
  margin-bottom: 1rem;
  line-height: 1.2;
  text-transform: uppercase;
  letter-spacing: 4px;
  color: #ffffff;

  @media (max-width: 768px) {
    font-size: 3.5rem;
    letter-spacing: 2px;
  }

  @media (max-width: 480px) {
    font-size: 2.5rem;
    letter-spacing: 1px;
    padding: 0 1rem;
  }
`;

const HeroSubtitle = styled.p`
  font-family: 'Poppins', sans-serif;
  font-size: 1.75rem;
  font-weight: 500;
  margin-bottom: 2rem;
  color: #cccccc;

  @media (max-width: 768px) {
    font-size: 1.25rem;
  }

  @media (max-width: 480px) {
    font-size: 1rem;
    padding: 0 1rem;
  }
`;

const HeroButton = styled.button`
  background-color: var(--primary-color);
  color: var(--highlight-color);
  padding: 1rem 2rem;
  border-radius: 30px;
  font-size: 1.25rem;
  font-weight: bold;
  font-family: 'Poppins', sans-serif;
  cursor: pointer;
  transition: all 0.3s ease;
  border: 2px solid transparent;

  &:hover {
    background-color: var(--secondary-color);
    border-color: var(--primary-color);
    transform: scale(1.05);
  }

  @media (max-width: 768px) {
    font-size: 1rem;
    padding: 0.8rem 1.5rem;
  }

  @media (max-width: 480px) {
    font-size: 0.9rem;
    padding: 0.7rem 1.2rem;
  }
`;

const handleSmoothScroll = (id) => {
  const element = document.getElementById(id);
  if (element) {
    element.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
    });
  }
};

const HeroSection = () => {
  useEffect(() => {
    const handleScroll = () => {
      const scrollY = window.scrollY;
      const maxScroll = 300;
      const opacity = Math.max(0, 1 - scrollY / maxScroll);

      document.querySelector('.hero-container > div').style.opacity = opacity;
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <HeroContainer id="hero" className="hero-container">
      <motion.div
        initial="hidden"
        whileInView="visible"
        viewport={{ once: false, amount: 0.3 }}
        variants={heroVariants}
      >
        <motion.div variants={titleVariants}>
          <HeroTitle>Find Your Dream Home</HeroTitle>
        </motion.div>
        <motion.div variants={subtitleVariants}>
          <HeroSubtitle>Discover your perfect property with our expert guidance.</HeroSubtitle>
        </motion.div>
        <motion.div variants={buttonVariants}>
          <HeroButton onClick={() => handleSmoothScroll('contact')}> Get In Touch</HeroButton>
        </motion.div>
      </motion.div>
    </HeroContainer>
  );
};

export default HeroSection;